import { Drawer } from '@base-ui/react/drawer';
import { Fragment } from 'react';
import { useNavigate } from 'react-router-dom';

import { notificationFilter } from '@/api/notifications/types';
import BellIcon from '@/assets/svg/header/bell.svg';
import CloseIcon from '@/assets/svg/ui-elements/close.svg';
import QRCodeIcon from '@/assets/svg/ui-elements/qr-code.svg';
import HeaderUserIcon from '@/assets/svg/ui-elements/user-outline.svg';
import { Separator, theme, Typography } from '@/design-system';
import { colors } from '@/design-system/colors';
import { useCommunicationsUnreadCount } from '@/global-hooks/use-communications-unread-count';
import { useGlobalModal } from '@/global-hooks/use-global-modal';
import { settingsTabKeys } from '@/pages/settings/settings-tab-keys';
import { useGetAllNotifications } from '@/pages/settings/utils/use-get-all-notifications';
import { PagePaths } from '@/routes/page-paths';
import { BugReportButton } from '@/shared/design-system/components/Sidebar/bug-report-button';
import { SidebarItem } from '@/shared/design-system/components/Sidebar/sidebar-item';
import { useGetSidebarLinks } from '@/shared/design-system/components/Sidebar/use-get-sidebar-links';
import { useUserAccount } from '@/shared/design-system/components/UserDropdown/use-user-account';
import {
  BadgeBar,
  BadgeRow,
  UserInfo,
} from '@/shared/design-system/components/UserDropdown/UserDropdown.styled';
import { useSelectedUserConfigSlice } from '@/stores/selectors';

import { NotificationBadge } from '../header-actions/HeaderActions.styled';
import {
  MobileActionButton,
  NavDrawerBackdrop,
  NavDrawerContent,
  NavDrawerHeader,
  NavDrawerPopup,
  NavDrawerQRButton,
  NavDrawerUserAvatar,
  NavDrawerUserRow,
} from './MobileHeader.styled';

type Props = {
  open: boolean;
  onOpenChange: (_open: boolean) => void;
  onUserAccountOpen: () => void;
  userAccount: ReturnType<typeof useUserAccount>;
};

export const NavDrawer = ({ open, onOpenChange, onUserAccountOpen, userAccount }: Props) => {
  const navigate = useNavigate();
  const { handleOpen: openQRCode } = useGlobalModal('QR_CODE_MODAL');
  const notifications = useGetAllNotifications({ filter: notificationFilter.NEW });
  const hasNotifications = notifications.length > 0;

  const { selectedEntityId, selectedUserRole } = useSelectedUserConfigSlice();
  const communicationsUnreadCount = useCommunicationsUnreadCount();

  const { headshotFile, roleConfig, badgeLabel, user } = userAccount;

  const links = useGetSidebarLinks({
    role: selectedUserRole,
    entityId: selectedEntityId || undefined,
    userId: user?.id,
    hasSubmittedQuestionnaire: user?.hasSubmittedQuestionnaire,
    isBlocked: user?.isBlocked,
    kycStatus: user?.kycStatus,
    isNonAccredited: user?.isNonAccredited,
  });

  const handleUserAccountOpen = () => {
    onOpenChange(false);
    onUserAccountOpen();
  };

  const handleQRCode = () => {
    onOpenChange(false);
    openQRCode();
  };

  const handleNotifications = () => {
    onOpenChange(false);
    navigate(`${PagePaths.settings}?tab=${settingsTabKeys.notifications}`);
  };

  return (
    <Drawer.Root open={open} onOpenChange={onOpenChange} swipeDirection="right" modal>
      <Drawer.Portal>
        <NavDrawerBackdrop />
        <NavDrawerPopup>
          <NavDrawerHeader>
            <NavDrawerUserRow onClick={handleUserAccountOpen} aria-label="Open account">
              <NavDrawerUserAvatar>
                {headshotFile?.url ? (
                  <img src={headshotFile.url} alt={headshotFile.fileName} />
                ) : (
                  <HeaderUserIcon color={colors.text.primary} />
                )}
              </NavDrawerUserAvatar>

              <UserInfo style={{ minWidth: 0 }}>
                <Typography variant="body4" fontWeight={500} noWrap>
                  {user?.name || user?.email}
                </Typography>
                <BadgeRow>
                  <BadgeBar $color={roleConfig.color} />
                  <Typography
                    variant="body5"
                    color={roleConfig.color}
                    noWrap
                    style={{ fontFamily: theme.typography.fontFamily.mono }}
                  >
                    {badgeLabel}
                  </Typography>
                </BadgeRow>
              </UserInfo>
            </NavDrawerUserRow>

            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <MobileActionButton aria-label="open notifications" onClick={handleNotifications}>
                <BellIcon />
                {hasNotifications && <NotificationBadge />}
              </MobileActionButton>

              <MobileActionButton aria-label="close menu" onClick={() => onOpenChange(false)}>
                <CloseIcon />
              </MobileActionButton>
            </div>
          </NavDrawerHeader>

          <NavDrawerQRButton onClick={handleQRCode} aria-label="Open QR code">
            <QRCodeIcon />
            <Typography variant="body3" fontWeight={500}>
              Your Personal QR Code
            </Typography>
          </NavDrawerQRButton>

          <NavDrawerContent>
            {links.map((linkGroup, idx) => (
              <Fragment key={`${idx}_${linkGroup.length}`}>
                {idx !== 0 && <Separator tone="borderSubtle" />}
                {linkGroup.map((link) => (
                  <SidebarItem
                    key={link.text}
                    {...link}
                    unreadCount={
                      link.href === PagePaths.communications ? communicationsUnreadCount : undefined
                    }
                    onClick={() => onOpenChange(false)}
                  />
                ))}
              </Fragment>
            ))}
          </NavDrawerContent>

          <BugReportButton />
        </NavDrawerPopup>
      </Drawer.Portal>
    </Drawer.Root>
  );
};
